import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlinePlus, HiOutlineKey, HiOutlineBan, HiOutlineCheck, HiOutlineLink, HiOutlineX } from 'react-icons/hi';

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [partnerManagers, setPartnerManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [showHubspot, setShowHubspot] = useState(null);
  const [hsEmail, setHsEmail] = useState('');
  const [hsResult, setHsResult] = useState(null);
  const [hsSearching, setHsSearching] = useState(false);
  const [newPartner, setNewPartner] = useState({ email: '', password: '', companyName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [search, setSearch] = useState('');
  const { addToast } = useToast();

  const loadPartners = () => {
    Promise.all([
      api.get('/admin/partners'),
      api.get('/admin/partner-managers')
    ]).then(([p, pm]) => {
      setPartners(p.data);
      setPartnerManagers(pm.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { loadPartners(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/partners', newPartner);
      addToast('Partner created successfully');
      setShowCreate(false);
      setNewPartner({ email: '', password: '', companyName: '' });
      loadPartners();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to create partner', 'error');
    }
  };

  const handleResetPassword = async (partnerId) => {
    if (!newPassword || newPassword.length < 8) {
      addToast('Password must be at least 8 characters', 'error');
      return;
    }
    try {
      await api.post(`/admin/partners/${partnerId}/reset-password`, { newPassword });
      addToast('Password reset successfully');
      setShowResetPw(null);
      setNewPassword('');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to reset password', 'error');
    }
  };

  const handleToggleStatus = async (partner) => {
    try {
      const newStatus = !partner.user.isActive;
      await api.put(`/admin/partners/${partner.id}/status`, { isActive: newStatus });
      addToast(`Partner ${newStatus ? 'enabled' : 'disabled'}`);
      loadPartners();
    } catch (err) {
      addToast('Failed to update status', 'error');
    }
  };

  const handleAssignPM = async (partnerId, pmId) => {
    try {
      await api.put(`/admin/partners/${partnerId}/assign-pm`, { partnerManagerId: pmId || null });
      addToast(pmId ? 'Partner Manager assigned' : 'Partner Manager removed');
      loadPartners();
    } catch (err) {
      addToast('Failed to assign PM', 'error');
    }
  };

  const handleHubspotSearch = async (partnerId) => {
    if (!hsEmail.trim()) { addToast('Enter an email to search', 'error'); return; }
    setHsSearching(true);
    setHsResult(null);
    try {
      const { data } = await api.post(`/admin/partners/${partnerId}/link-hubspot`, { email: hsEmail });
      setHsResult(data);
      addToast('Partner linked to HubSpot contact');
      loadPartners();
    } catch (err) {
      addToast(err.response?.data?.error || 'HubSpot search failed', 'error');
    }
    setHsSearching(false);
  };

  const handleHubspotUnlink = async (partnerId) => {
    try {
      await api.post(`/admin/partners/${partnerId}/unlink-hubspot`);
      addToast('HubSpot contact unlinked');
      loadPartners();
    } catch (err) {
      addToast('Failed to unlink', 'error');
    }
  };

  const filtered = partners.filter(p =>
    p.companyName.toLowerCase().includes(search.toLowerCase()) ||
    p.user.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
          <p className="text-gray-500">{partners.length} total partners</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700">
          <HiOutlinePlus className="w-5 h-5" /> Add Partner
        </button>
      </div>

      <input
        type="text"
        placeholder="Search partners..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-v2-blue outline-none"
      />

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Create Partner Account</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" required value={newPartner.email} onChange={e => setNewPartner({...newPartner, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
                <input type="text" required minLength={8} value={newPartner.password} onChange={e => setNewPartner({...newPartner, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input type="text" required value={newPartner.companyName} onChange={e => setNewPartner({...newPartner, companyName: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-v2-blue text-white py-2 rounded-lg font-medium hover:bg-blue-700">Create</button>
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-gray-300 py-2 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HubSpot link modal */}
      {showHubspot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => { setShowHubspot(null); setHsResult(null); setHsEmail(''); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Link to HubSpot</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search by Email</label>
                <div className="flex gap-2">
                  <input type="email" value={hsEmail} onChange={e => setHsEmail(e.target.value)} placeholder="contact@company.com"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
                  <button onClick={() => handleHubspotSearch(showHubspot)} disabled={hsSearching}
                    className="px-4 py-2 bg-v2-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {hsSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
              {hsResult && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800">Contact linked successfully:</p>
                  <p className="text-sm text-green-700">{hsResult.contactName} ({hsResult.contactEmail})</p>
                  {hsResult.companyName && <p className="text-sm text-green-600">Company: {hsResult.companyName}</p>}
                </div>
              )}
              <button onClick={() => { setShowHubspot(null); setHsResult(null); setHsEmail(''); }}
                className="w-full border border-gray-300 py-2 rounded-lg font-medium hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Partners table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Assets</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Partner Manager</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">HubSpot</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(partner => (
                <tr key={partner.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{partner.companyName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{partner.user.email}</td>
                  <td className="px-4 py-3 text-sm">{partner._count.generatedAssets}</td>
                  <td className="px-4 py-3">
                    <select
                      value={partner.partnerManager?.id || ''}
                      onChange={e => handleAssignPM(partner.id, e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-v2-blue outline-none"
                    >
                      <option value="">Unassigned</option>
                      {partnerManagers.map(pm => (
                        <option key={pm.id} value={pm.id}>{pm.displayName}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {partner.hubspotContactId ? (
                      <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800 font-medium">Linked</span>
                        <button onClick={() => handleHubspotUnlink(partner.id)} className="text-gray-400 hover:text-red-500" title="Unlink">
                          <HiOutlineX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => { setShowHubspot(partner.id); setHsEmail(partner.user.email); }}
                        className="text-gray-400 hover:text-orange-500" title="Link to HubSpot">
                        <HiOutlineLink className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      partner.user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {partner.user.isActive ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-center">
                      <button onClick={() => { setShowResetPw(partner.id); setNewPassword(''); }} className="text-gray-400 hover:text-v2-blue" title="Reset password">
                        <HiOutlineKey className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleToggleStatus(partner)} className={`${partner.user.isActive ? 'text-gray-400 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`} title={partner.user.isActive ? 'Disable' : 'Enable'}>
                        {partner.user.isActive ? <HiOutlineBan className="w-5 h-5" /> : <HiOutlineCheck className="w-5 h-5" />}
                      </button>
                    </div>
                    {showResetPw === partner.id && (
                      <div className="mt-2 flex gap-2">
                        <input type="text" placeholder="New password (min 8)" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded text-sm flex-1 focus:ring-2 focus:ring-v2-blue outline-none" />
                        <button onClick={() => handleResetPassword(partner.id)} className="px-3 py-1 bg-v2-blue text-white rounded text-sm hover:bg-blue-700">Reset</button>
                        <button onClick={() => setShowResetPw(null)} className="px-3 py-1 border rounded text-sm hover:bg-gray-50">Cancel</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
